/**
 * Move operations — change a task's parentPath with full cascade.
 *
 * The destination is validated here, in the shared op (the app, REST, MCP and the AI tools all
 * come through it): it must be a path the app builds — `root/<container-id>`, `root/#inbox`,
 * `root/#anyday` — to a live container that object may live in, not the object itself or one of
 * its descendants. Anything else would leave the task under a path no view reads, i.e. orphaned.
 */

import { ObjectStatus, ObjectType } from "./db-types.js"
import { getDb, callUndo, getNow, emitEvent } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult, MoveOpts } from "./op-types.js"
import type { DbObject } from "./db-types.js"
import { getInsertIndex, computeGhostIndex, firstAccepted } from "./helpers/indexHelpers.js"
import { cascadeDescendants } from "./helpers/cascadeHelpers.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"
import { serializeTaskMoves } from "./helpers/queueHelpers.js"
import { assertValidMoveTarget, checkMoveTarget, resolveMoveTarget } from "./helpers/validationHelpers.js"
import { reopenContainerIfClosed } from "./containerStatusOps.js"

/**
 * An undo or redo step that did not work must say so. The client's undo manager reports a failed
 * step by it throwing (it logs, keeps the entry on its stack and shows no "Undone" toast); a step
 * that swallowed a refused move would be reported as done although nothing moved.
 */
function mustSucceed(result: OpResult<unknown>): void {
  if (!result.ok) throw new Error(result.error)
}

// ---------------------------------------------------------------------------
// moveTask — full move with cascade, realm, ghost-index
// ---------------------------------------------------------------------------

export async function moveTask(
  id: string,
  newParentPath: string,
  opts?: MoveOpts,
): Promise<OpResult> {
  try {
    // Read the current path and cascade from it after any other move of this task
    // (see serializeTaskMoves): an overlapping move would cascade from a stale path.
    const moved = await serializeTaskMoves([id], () => moveTaskNow(id, newParentPath, opts))
    if (!moved.ok || moved.data === null) return moved.ok ? ok() : moved
    const oldParentPath = moved.data

    callUndo(
      "Moved task",
      async () => {
        mustSucceed(await moveTask(id, oldParentPath, {
          insertPosition: { afterId: id },
        }))
      },
      async () => {
        mustSucceed(await moveTask(id, newParentPath, opts))
      },
      "update",
    )

    emitEvent("task_moved", { id, from: oldParentPath, to: newParentPath })
    return ok()
  } catch (e) {
    return fail(
      `moveTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/** moveTask's reads and writes; the old parentPath, or null when the task already is there. */
async function moveTaskNow(
  id: string,
  newParentPath: string,
  opts?: MoveOpts,
): Promise<OpResult<string | null>> {
  const db = getDb()
  const task = await db.totodo.get(id)
  if (!task) return fail(`Task not found: ${id}`)

  const oldParentPath = task.parentPath

  // Idempotent: same container → no-op
  if (oldParentPath === newParentPath) return ok(null)

  // A destination that exists and may hold it (reads only: nothing is written before this)
  const invalid = await assertValidMoveTarget(task, newParentPath)
  if (invalid) return invalid

  // An open task moved into a closed project / list reopens it (first: see reopenContainerIfClosed)
  if (task.type === ObjectType.Task && task.status === ObjectStatus.Unfinished) {
    await reopenContainerIfClosed(newParentPath)
  }

  const timestamp = getNow()

  // 1. Calculate new index in target container
  const newIndex = await getInsertIndex(
    newParentPath,
    opts?.insertPosition ?? "bottom",
    task.type as ObjectType,
  )

  // 2. Resolve realmId from target container
  const newRealmId = await resolveContainerRealmId(newParentPath)

  // 3. Write ghost-index in secondary dimension
  const ghostIndex = await computeGhostIndex("startAtIndex")

  await db.transaction(
    "rw",
    [db.totodo, db.startAtIndex, db.privateIndex],
    async (tx) => {
      // Update the task itself
      await db.totodo.update(id, {
        parentPath: newParentPath,
        index: newIndex,
        ...(newRealmId !== task.realmId ? { realmId: newRealmId } : {}),
        updatedAt: timestamp,
      })

      // Write ghost to startAtIndex if entry exists
      const entryId = `#${id}`
      const existing = await db.startAtIndex.get(entryId)
      if (existing) {
        await db.startAtIndex.update(entryId, { index: ghostIndex })
      }

      // privateIndex for shared containers (v1 parity)
      const currentUserId = db.cloud.currentUserId
      if (
        newRealmId &&
        newRealmId !== currentUserId &&
        (task.type === ObjectType.Project || task.type === ObjectType.List)
      ) {
        await db.privateIndex.put({ id: entryId, index: newIndex })
      }

      // 4. Cascade to all descendants
      await cascadeDescendants(
        id,
        oldParentPath,
        newParentPath,
        newRealmId,
        tx,
      )
    },
  )

  return ok(oldParentPath)
}

// ---------------------------------------------------------------------------
// moveMultiple — bulk move with cascade
// ---------------------------------------------------------------------------

export async function moveMultiple(
  ids: string[],
  newParentPath: string,
  opts?: MoveOpts,
): Promise<OpResult<{ count: number }>> {
  if (ids.length === 0) return ok({ count: 0 })

  try {
    // After any other move of one of these tasks (see serializeTaskMoves)
    const moved = await serializeTaskMoves(ids, () =>
      moveMultipleNow(ids, newParentPath, opts),
    )
    if (!moved.ok) return moved
    const validTasks = moved.data
    if (validTasks.length === 0) return ok({ count: 0 })

    // Register undo for bulk move
    const moveBackData = validTasks.map((t) => ({
      id: t.id as string,
      prevParentPath: t.parentPath,
    }))
    callUndo(
      `Moved ${validTasks.length} tasks`,
      async () => {
        // Move back as many as can be moved, then report the first one that could not.
        let failed: OpResult<unknown> | undefined
        for (const item of moveBackData) {
          const result = await moveTask(item.id, item.prevParentPath)
          if (!result.ok) failed ??= result
        }
        if (failed) mustSucceed(failed)
      },
      async () => {
        mustSucceed(await moveMultiple(ids, newParentPath, opts))
      },
      "batch",
    )

    return ok({ count: validTasks.length })
  } catch (e) {
    return fail(
      `moveMultiple failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/**
 * moveMultiple's reads and writes; the tasks it moved, as they were before. All or nothing: one
 * task that may not go to the destination refuses the whole move, before anything is written.
 */
async function moveMultipleNow(
  ids: string[],
  newParentPath: string,
  opts?: MoveOpts,
): Promise<OpResult<DbObject[]>> {
  const db = getDb()
  const timestamp = getNow()

  // Get all tasks first
  const tasks = await Promise.all(ids.map((id) => db.totodo.get(id)))
  const validTasks = tasks.filter(
    (t): t is NonNullable<typeof t> =>
      t != null && t.parentPath !== newParentPath,
  )

  if (validTasks.length === 0) return ok([])

  // The destination, once; then each task against it (reads only)
  const target = await resolveMoveTarget(newParentPath)
  if ("ok" in target) return target
  for (const task of validTasks) {
    const invalid = checkMoveTarget(task, newParentPath, target)
    if (invalid) return invalid
  }

  // An open task moved into a closed project / list reopens it (first: see reopenContainerIfClosed)
  if (validTasks.some((t) => t.type === ObjectType.Task && t.status === ObjectStatus.Unfinished)) {
    await reopenContainerIfClosed(newParentPath)
  }

  const resolvedRealm = await resolveContainerRealmId(newParentPath)

  // Calculate base index in target container
  const position = opts?.insertPosition ?? "bottom"
  const baseIndex = await getInsertIndex(
    newParentPath,
    position,
    ObjectType.Task,
  )

  // Calculate spacing so all items fit in the gap before the next sibling.
  let spacing = 1
  if (position !== "bottom" && validTasks.length > 1) {
    // The first live row above the insert point that is not itself being moved. A LIMIT window is
    // enough unless it is all moved tasks that are already siblings (and trashed rows): see firstAccepted.
    const movingIds = new Set(ids)
    const alreadyThere = tasks.filter((t) => t?.parentPath === newParentPath).length
    const ceilingSibling = await firstAccepted(
      () =>
        db.totodo
          .where("[parentPath+index]")
          .between([newParentPath, baseIndex], [newParentPath, Infinity], false, true),
      (o) => o.isDeleted === 0 && !movingIds.has(o.id as string),
      alreadyThere + 8,
    )

    if (ceilingSibling && ceilingSibling.index > baseIndex) {
      spacing = (ceilingSibling.index - baseIndex) / (validTasks.length + 1)
    }
  }

  const ghostIndex = await computeGhostIndex("startAtIndex")

  await db.transaction("rw", [db.totodo, db.startAtIndex], async (tx) => {
    for (let i = 0; i < validTasks.length; i++) {
      const task = validTasks[i]!
      const taskId = task.id as string
      const oldParentPath = task.parentPath
      const newRealmId = resolvedRealm

      await db.totodo.update(taskId, {
        parentPath: newParentPath,
        index: baseIndex + i * spacing,
        ...(newRealmId !== task.realmId ? { realmId: newRealmId } : {}),
        updatedAt: timestamp,
      })

      // Ghost index for startAtIndex
      const entryId = `#${taskId}`
      const existing = await db.startAtIndex.get(entryId)
      if (existing) {
        await db.startAtIndex.update(entryId, { index: ghostIndex - i })
      }

      // Cascade descendants
      await cascadeDescendants(
        taskId,
        oldParentPath,
        newParentPath,
        newRealmId,
        tx,
      )
    }
  })

  return ok(validTasks)
}
